import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY, lll } from '@/lib/format'
import type { StaffUser } from '@/api/map/staffUsers'
import { useStaffUsers } from '@/api/hooks/staff/useStaffUsers'
import { useCmMe } from '@/api/hooks/useCmMe'
import {
  canCreateStaffUsers,
  canEditStaffUsers,
  canManageAccessRoles,
} from '@/app/staffAccess'
import './users.css'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function UsersPage() {
  const navigate = useNavigate()
  const me = useCmMe()
  const showManageRoles = canManageAccessRoles(me.data?.caller)
  const canCreate = canCreateStaffUsers(me.data?.caller)
  const canEdit = canEditStaffUsers(me.data?.caller)
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useStaffUsers({
    page,
    keyword: keyword.trim() || undefined,
  })

  const columns = useMemo<Column<StaffUser>[]>(
    () => [
      {
        id: 'username',
        header: 'User name',
        cell: (row) =>
          canEdit ? (
            <Link to={`/users/${row.id}`} className="w7-users-link">
              {row.username}
            </Link>
          ) : (
            row.username
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <Chip tone={row.status === 'active' ? 'profit' : 'muted'}>
            {row.status === 'active' ? 'Active' : 'Inactive'}
          </Chip>
        ),
      },
      {
        id: 'access',
        header: 'Grants',
        cell: (row) => {
          const n = row.grants?.length ?? 0
          if (!n) return EMPTY
          return `${n} action${n === 1 ? '' : 's'}`
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: (row) => lll(row.updatedAt),
      },
      ...(canEdit
        ? [
            {
              id: 'edit',
              header: '',
              cell: (row: StaffUser) => (
                <Button variant="ghost" size="sm" onClick={() => navigate(`/users/${row.id}`)}>
                  Edit
                </Button>
              ),
            } satisfies Column<StaffUser>,
          ]
        : []),
    ],
    [navigate, canEdit],
  )
  const results = data?.results ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize ?? PAGE_SIZE)))
  const narrowed = Boolean(keyword.trim())

  return (
    <div className="w7-users-page">
      <PageHead
        title="User Access Level"
        subtitle="Staff logins under this CM — Menu → Access Role → actions. Catalog / Market Selection are CM-only; Premium BM Management is omitted until integrated."
        actions={
          <>
            {showManageRoles ? (
              <Button variant="ghost" onClick={() => navigate('/users/access-roles')}>
                Manage Access Roles
              </Button>
            ) : null}
            {canCreate ? (
              <Button variant="primary" onClick={() => navigate('/users/new')}>
                Create user
              </Button>
            ) : null}
          </>
        }
      />

      <Banner tone="info">
        Stored on staff_users in the shared hierarchy database. CM-API does not
        call Agent-API. Access Level users sign in on this console login page
        with username and password; Company Masters keep Keycloak.
      </Banner>

      <Card flush className="w7-users-panel">
        <div className="w7-users-toolbar">
          <SearchInput
            label="Search staff users"
            value={keyword}
            onChange={(value) => {
              setKeyword(value)
              setPage(1)
            }}
            placeholder="Search username"
            width={240}
          />
          <span className="w7-users-count" aria-live="polite">
            {isLoading && total === 0 ? '…' : `${count(total)} users`}
          </span>
        </div>

        {isError ? (
          <div className="w7-users-empty">
            <StateBlock icon="users" title="Could not load users" tone="error">
              Check CM-API and try again.
            </StateBlock>
          </div>
        ) : (
          <>
            <DataTable
              label="Staff users"
              columns={columns}
              rows={results}
              rowKey={(r) => r.id}
              loading={isLoading}
              stickyOffset={0}
              empty={
                <StateBlock
                  icon="users"
                  title={narrowed ? 'No users match this search' : 'No staff users yet'}
                  actions={
                    narrowed ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setKeyword('')
                          setPage(1)
                        }}
                      >
                        Clear search
                      </Button>
                    ) : canCreate ? (
                      <Button variant="primary" onClick={() => navigate('/users/new')}>
                        Create user
                      </Button>
                    ) : undefined
                  }
                >
                  {narrowed
                    ? 'Try another username, or clear the search to see everyone.'
                    : canCreate
                      ? 'Create a user to give someone limited CM console access.'
                      : 'No staff users yet.'}
                </StateBlock>
              }
            />
            {total > 0 || isLoading ? (
              <div className="w7-users-foot">
                <Pager
                  page={page}
                  pageCount={pageCount}
                  onChange={setPage}
                  total={total}
                  pageSize={data?.pageSize ?? PAGE_SIZE}
                />
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
